import { z } from "zod"

/**
 * Zod schemas for dashboard components.
 * These define the valid JSON spec that the AI can return.
 */

const colorSchema = z.string().optional()

/**
 * Color threshold for KPI / bar tinting — Grafana-style. Each entry
 * sets the colour from `value` upward; the highest matching bracket
 * wins. Common shape:
 *   [{value: 0, color: "#73BF69"},
 *    {value: 80, color: "#FF9830"},
 *    {value: 95, color: "#F2495C"}]
 * meaning: <80 → green, 80–95 → amber, ≥95 → red.
 *
 * Negative thresholds are allowed for delta-style KPIs (e.g. revenue
 * change percentage). Empty array disables threshold colouring and
 * the renderer falls back to its default accent.
 */
export const colorThresholdSchema = z.object({
  value: z.number(),
  color: z.string(),
})
export type ColorThreshold = z.infer<typeof colorThresholdSchema>

const dataPointSchema = z.record(z.string(), z.union([z.string(), z.number(), z.null()]))

/**
 * Click-to-filter descriptor — when present on a chart, clicking a
 * bar / line point / pie segment writes the clicked datum's `valueKey`
 * column into the dashboard state at `stateKey`. The auto-refresh
 * wire on filter changes picks it up and re-runs every binding whose
 * `params_from_state` references that path. Same UX Power BI gives
 * out of the box.
 *
 * `valueKey` defaults to the chart's `xKey` if omitted — the natural
 * filter axis. Authors can override to filter on a secondary column
 * (e.g. category, region) by setting an explicit key.
 */
export const chartClickFilterSchema = z.object({
  stateKey: z.string(),
  valueKey: z.string().optional(),
})
export type ChartClickFilter = z.infer<typeof chartClickFilterSchema>

// Drill-through descriptor. Hoisted up here so it's available to the
// chart schemas declared just below — Table picks it up further down
// via the same name. Schema details mirror the Table definition; see
// the tableSchema block for the full semantics + token rules.
export const drillThroughSchema = z.object({
  title: z.string().optional(),
  sql: z.string(),
  connection_id: z.string(),
  params_from_row: z.record(z.string(), z.string()).optional(),
})
export type DrillThrough = z.infer<typeof drillThroughSchema>

// ─── Expression Support ─────────────────────────────────────────────

const expressionSchema = z.union([
  z.object({ $state: z.string() }).passthrough(),
  z.object({ $bindState: z.string() }).passthrough(),
  z.object({ $cond: z.unknown(), $then: z.unknown() }).passthrough(),
  z.object({ $template: z.string() }).passthrough(),
  z.object({ $computed: z.string() }).passthrough(),
  z.object({ $filter: z.unknown(), where: z.record(z.string(), z.unknown()) }).passthrough(),
])

/** Allow a field to accept either a plain value or an expression object. */
function exprOr<T extends z.ZodTypeAny>(schema: T) {
  return z.union([schema, expressionSchema])
}

// ─── Visibility Condition ──────────────────────────────────────────

// Note: $and / $or are typed as `unknown[]` because the inner items
// recurse on the same union. Zod's lazy-recursive types in v4 make
// this awkward to express; we accept unknown[] and rely on the
// runtime evaluator to validate sub-shapes.
export const visibilityConditionSchema = z.union([
  z.boolean(),
  z.object({ $state: z.string(), $default: z.unknown().optional() }),
  z.object({
    $cond: z.unknown(),
    $then: z.unknown(),
    $else: z.unknown().optional(),
  }),
  z.object({ $and: z.array(z.unknown()) }),
  z.object({ $or: z.array(z.unknown()) }),
])

/** Responsive width for a top-level dashboard tile. Nested Grid/Row
 * children keep using their container's layout rules. Missing means
 * full width, preserving every existing persisted dashboard. */
export const tileWidthSchema = z.enum(["full", "half"])
export type TileWidth = z.infer<typeof tileWidthSchema>

// ─── Chart Components ───────────────────────────────────────────────

// Data-bearing element schemas use `.passthrough()` so the agent's
// inline binding hints — `_sql`, `_connectionId`, `_targetField`,
// `_paramsFromState` — survive Zod's default strip-unknown-keys
// behaviour and reach the backend's `extract_inline_bindings()` on
// dashboard save. Without passthrough, the underscore fields are
// silently dropped client-side and no query_bindings get created.
export const barChartSchema = z
  .object({
    type: z.literal("BarChart"),
    width: tileWidthSchema.optional(),
    title: exprOr(z.string()).optional(),
    data: exprOr(z.array(dataPointSchema)),
    xKey: z.string(),
    bars: z.array(
      z.object({
        dataKey: z.string(),
        color: colorSchema,
        label: z.string().optional(),
        stackId: z.string().optional(),
        /**
         * Optional per-bar threshold colouring. When present, each bar's
         * fill is picked from the highest threshold the bar's numeric
         * value is at-or-above (overrides the gradient). Shape mirrors
         * KPICard.thresholds so authors learn one mental model.
         */
        thresholds: z.array(colorThresholdSchema).optional(),
      })
    ),
    layout: z.enum(["horizontal", "vertical"]).optional(),
    onClickFilter: chartClickFilterSchema.optional(),
    drillThrough: drillThroughSchema.optional(),
    visible: visibilityConditionSchema.optional(),
  })
  .passthrough()

export const lineChartSchema = z
  .object({
    type: z.literal("LineChart"),
    width: tileWidthSchema.optional(),
    title: exprOr(z.string()).optional(),
    data: exprOr(z.array(dataPointSchema)),
    xKey: z.string(),
    lines: z.array(
      z.object({
        dataKey: z.string(),
        color: colorSchema,
        label: z.string().optional(),
        strokeDasharray: z.string().optional(),
      })
    ),
    onClickFilter: chartClickFilterSchema.optional(),
    visible: visibilityConditionSchema.optional(),
  })
  .passthrough()

export const areaChartSchema = z
  .object({
    type: z.literal("AreaChart"),
    width: tileWidthSchema.optional(),
    title: exprOr(z.string()).optional(),
    data: exprOr(z.array(dataPointSchema)),
    xKey: z.string(),
    areas: z.array(
      z.object({
        dataKey: z.string(),
        color: colorSchema,
        label: z.string().optional(),
        stackId: z.string().optional(),
      })
    ),
    visible: visibilityConditionSchema.optional(),
  })
  .passthrough()

export const pieChartSchema = z
  .object({
    type: z.literal("PieChart"),
    width: tileWidthSchema.optional(),
    title: exprOr(z.string()).optional(),
    data: exprOr(
      z.array(
        z.object({
          name: z.string(),
          // pymysql returns SUM(decimal) as a string ("51285107.88"). Accept
          // both number and numeric string here — same as `dataPointSchema`
          // which the other chart types use. The renderer coerces with
          // parseFloat when slicing the pie.
          value: z.union([z.number(), z.string()]),
          color: colorSchema,
        })
      )
    ),
    innerRadius: z.number().optional(),
    outerRadius: z.number().optional(),
    onClickFilter: chartClickFilterSchema.optional(),
    drillThrough: drillThroughSchema.optional(),
    visible: visibilityConditionSchema.optional(),
  })
  .passthrough()

// ─── Data Components ────────────────────────────────────────────────

export const kpiCardSchema = z
  .object({
    type: z.literal("KPICard"),
    width: tileWidthSchema.optional(),
    title: exprOr(z.string()),
    value: exprOr(z.union([z.string(), z.number()])),
    unit: exprOr(z.string()).optional(),
    change: exprOr(z.number()).optional(),
    changeLabel: exprOr(z.string()).optional(),
    icon: z.string().optional(),
    /**
     * Optional trend mini-chart drawn below the value, Grafana-stat-panel
     * style. Each entry is a single numeric reading; the renderer
     * normalises the series to the card width and draws a smooth
     * polyline. ~5–60 points reads cleanly; longer series get downsampled
     * by the renderer so the chart never becomes a wall of pixels.
     */
    sparkline: z.array(z.number()).optional(),
    /**
     * Optional accent for the sparkline stroke / area. When omitted the
     * sparkline inherits the card's threshold-driven accent (or primary
     * if no trend is present). CSS colour string, hex or hsl().
     */
    sparkline_color: z.string().optional(),
    /**
     * Color thresholds — when set, the value text + accent stripe pick
     * the colour of the highest threshold the value is at-or-above.
     * See colorThresholdSchema for shape + semantics.
     */
    thresholds: z.array(colorThresholdSchema).optional(),
    visible: visibilityConditionSchema.optional(),
  })
  .passthrough()

// drillThroughSchema is hoisted to the top of this file so the chart
// schemas above can reference it. The Table catalog below uses the
// same descriptor without redefining it.

export const tableSchema = z
  .object({
    type: z.literal("Table"),
    width: tileWidthSchema.optional(),
    title: exprOr(z.string()).optional(),
    columns: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        align: z.enum(["left", "center", "right"]).optional(),
      })
    ),
    rows: exprOr(z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()])))),
    drillThrough: drillThroughSchema.optional(),
    visible: visibilityConditionSchema.optional(),
  })
  .passthrough()

export const textSchema = z.object({
  type: z.literal("Text"),
  width: tileWidthSchema.optional(),
  content: exprOr(z.string()),
  variant: z.enum(["heading", "subheading", "body", "caption"]).optional(),
  visible: visibilityConditionSchema.optional(),
})

export const separatorSchema = z.object({
  type: z.literal("Separator"),
  label: z.string().optional(),
  visible: visibilityConditionSchema.optional(),
})

export const alertSchema = z.object({
  type: z.literal("Alert"),
  width: tileWidthSchema.optional(),
  message: exprOr(z.string()),
  title: exprOr(z.string()).optional(),
  variant: z.enum(["info", "warning", "success", "error"]).optional(),
  visible: visibilityConditionSchema.optional(),
})

export const progressBarSchema = z.object({
  type: z.literal("ProgressBar"),
  width: tileWidthSchema.optional(),
  value: exprOr(z.number()),
  max: exprOr(z.number()).optional(),
  label: exprOr(z.string()).optional(),
  color: z.string().optional(),
  visible: visibilityConditionSchema.optional(),
})

// Web/AI-research tile — its `content` + `sources` are produced by a
// web_research query binding (web search + LLM synthesis) on refresh,
// not authored inline. `content` is empty until the first refresh.
export const researchSummarySchema = z.object({
  type: z.literal("ResearchSummary"),
  title: exprOr(z.string()).optional(),
  content: exprOr(z.string()).optional(),
  sources: z.array(z.object({ title: z.string().optional(), url: z.string() })).optional(),
  visible: visibilityConditionSchema.optional(),
})

// ─── Layout Components ──────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gridSchema: z.ZodType<any> = z.object({
  type: z.literal("Grid"),
  columns: z.number().min(1).max(4).optional(),
  gap: z.number().optional(),
  children: z.lazy(() => z.array(dashboardElementSchema)),
  visible: visibilityConditionSchema.optional(),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const rowSchema: z.ZodType<any> = z.object({
  type: z.literal("Row"),
  gap: z.number().optional(),
  children: z.lazy(() => z.array(dashboardElementSchema)),
  visible: visibilityConditionSchema.optional(),
})

// ─── Interactive Layout Components ──────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tabsSchema: z.ZodType<any> = z.object({
  type: z.literal("Tabs"),
  tabs: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      children: z.lazy(() => z.array(dashboardElementSchema)),
    })
  ),
  defaultValue: z.string().optional(),
  visible: visibilityConditionSchema.optional(),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const accordionSchema: z.ZodType<any> = z.object({
  type: z.literal("Accordion"),
  items: z.array(
    z.object({
      title: z.string(),
      children: z.lazy(() => z.array(dashboardElementSchema)),
    })
  ),
  visible: visibilityConditionSchema.optional(),
})

// ─── Interactive Input Components ───────────────────────────────────

// Field-level validation declaration. Inputs that opt in surface
// inline errors at the configured trigger; see utils/validation.ts.
const validationCheckSchema = z.object({
  type: z.string(),
  message: z.string(),
  args: z.record(z.string(), z.unknown()).optional(),
})
const validateOnSchema = z.enum(["change", "blur", "submit"]).optional()

export const selectSchema = z.object({
  type: z.literal("Select"),
  stateKey: z.string(),
  label: z.string().optional(),
  options: z.array(z.object({ label: z.string(), value: z.string() })),
  defaultValue: z.string().optional(),
  placeholder: z.string().optional(),
  visible: visibilityConditionSchema.optional(),
  checks: z.array(validationCheckSchema).optional(),
  validateOn: validateOnSchema,
})

export const checkboxSchema = z.object({
  type: z.literal("Checkbox"),
  stateKey: z.string(),
  label: z.string(),
  defaultValue: z.boolean().optional(),
  visible: visibilityConditionSchema.optional(),
  checks: z.array(validationCheckSchema).optional(),
  validateOn: validateOnSchema,
})

export const textInputSchema = z.object({
  type: z.literal("TextInput"),
  stateKey: z.string(),
  label: z.string().optional(),
  placeholder: z.string().optional(),
  defaultValue: z.string().optional(),
  visible: visibilityConditionSchema.optional(),
  checks: z.array(validationCheckSchema).optional(),
  validateOn: validateOnSchema,
})

// ─── shadcn primitives (§3.2) ───────────────────────────────────────
// Lightweight non-chart components ported from shadcn. They round out
// the catalog so the AI can compose richer non-chart content (activity
// cards, user lists, status pills) without us inventing bespoke types.

export const headingSchema = z.object({
  type: z.literal("Heading"),
  text: exprOr(z.string()),
  level: z
    .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)])
    .optional(),
  visible: visibilityConditionSchema.optional(),
})

export const badgeSchema = z.object({
  type: z.literal("Badge"),
  text: exprOr(z.string()),
  variant: z.enum(["default", "secondary", "destructive", "outline"]).optional(),
  visible: visibilityConditionSchema.optional(),
})

export const avatarSchema = z.object({
  type: z.literal("Avatar"),
  /** Image URL; falls back to initials when missing or fails to load. */
  src: exprOr(z.string()).optional(),
  /** Display name — used to derive fallback initials. */
  name: exprOr(z.string()),
  size: z.enum(["sm", "md", "lg"]).optional(),
  visible: visibilityConditionSchema.optional(),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tooltipSchema: z.ZodType<any> = z.object({
  type: z.literal("Tooltip"),
  /** The text shown in the tooltip popover when the child is hovered. */
  content: exprOr(z.string()),
  /** Single child element the tooltip wraps. */
  child: z.lazy(() => dashboardElementSchema),
  visible: visibilityConditionSchema.optional(),
})

// ─── Repeat ─────────────────────────────────────────────────────────
//
// Render the `item` template once per element of `source`. Inside the
// body, `{$item: "field"}` reads a property of the current row and
// `{$index: true}` returns the row index. Source can be any expression
// (`$state`, `$filter`, etc.) — typically a state-bound array.
//
// Use this when you'd otherwise need the AI to flatten a list into
// N concrete elements at generation time (one Card per customer,
// one row per region). Repeat lets the AI emit one row template
// and the runtime fans it out — cheaper to generate, reactive to
// data changes, and decoupled from row count.

export const repeatSchema = z.object({
  type: z.literal("Repeat"),
  source: z.unknown(),
  item: z.unknown(),
  // Optional stable-key hint; the renderer falls back to the index
  // when not provided.
  keyField: z.string().optional(),
  visible: visibilityConditionSchema.optional(),
})

// ─── Union Schema ───────────────────────────────────────────────────

// Use z.union instead of z.discriminatedUnion for broader compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dashboardElementSchema: z.ZodType<any> = z.union([
  barChartSchema,
  lineChartSchema,
  areaChartSchema,
  pieChartSchema,
  kpiCardSchema,
  tableSchema,
  textSchema,
  separatorSchema,
  alertSchema,
  progressBarSchema,
  researchSummarySchema,
  gridSchema,
  rowSchema,
  tabsSchema,
  accordionSchema,
  selectSchema,
  checkboxSchema,
  textInputSchema,
  repeatSchema,
  headingSchema,
  badgeSchema,
  avatarSchema,
  tooltipSchema,
])

/**
 * Assertions on a queryBinding's result set. The agent emits these
 * alongside the SQL ("revenue should be positive", "row count should
 * be > 0 for the last 30 days"); a nightly evaluator re-runs the
 * binding and checks each assertion against the fresh rows. A failed
 * assertion is the early-warning signal that an upstream schema
 * change broke a tile — caught hours/days before the user opens the
 * dashboard and sees "no data".
 *
 * Kept tiny on purpose. Five `kind`s cover the bulk of useful checks
 * without us reinventing a constraint DSL:
 *
 * - **row_count**: row count of the result set vs op/value.
 *   `{kind:"row_count", op:">", value:0}` (the "is anything coming
 *   out at all" check)
 * - **column_min**: min value in a numeric column vs op/value.
 *   `{kind:"column_min", column:"revenue", op:">=", value:0}`
 * - **column_max**: max value in a numeric column vs op/value.
 * - **column_sum**: sum of a numeric column vs op/value.
 * - **column_non_null**: every row has a non-null value for the column.
 *   `{kind:"column_non_null", column:"order_id"}` (cheap data-integrity
 *   check that catches a refactor introducing rows from a left-join
 *   gone wrong)
 *
 * Op set is the same on every kind: `>`, `>=`, `<`, `<=`, `==`, `!=`.
 * Value is a number. The evaluator coerces row values to number when
 * possible; a non-numeric column value flips the assertion to fail
 * with a clear message rather than silently passing.
 */
export const bindingAssertionSchema = z.object({
  /** Stable id; lets the dispatcher de-duplicate runs across nightly jobs. */
  id: z.string(),
  /** Human-readable rationale for this check. Surfaced in alerts. */
  description: z.string(),
  kind: z.enum(["row_count", "column_min", "column_max", "column_sum", "column_non_null"]),
  /** Required for column_* kinds; ignored otherwise. */
  column: z.string().optional(),
  op: z.enum([">", ">=", "<", "<=", "==", "!="]).optional(),
  value: z.number().optional(),
  /** Set on first run; updated when the assertion last passed/failed. */
  last_run: z.string().optional(),
  last_status: z.enum(["pass", "fail"]).nullable().optional(),
  last_error: z.string().nullable().optional(),
})

export type BindingAssertion = z.infer<typeof bindingAssertionSchema>

export const queryBindingSchema = z.object({
  binding_id: z.string(),
  element_path: z.string(),
  target_field: z.string(),
  /**
   * Binding kind. Absent / "sql" → SQL-backed (needs `sql` + `connection_id`).
   * "web_research" → web/AI-research-backed (needs `research_prompt`); the
   * refresh path runs web search + LLM synthesis instead of a query.
   */
  binding_type: z.enum(["sql", "web_research"]).optional(),
  // SQL bindings only — optional so web_research bindings validate.
  sql: z.string().optional(),
  connection_id: z.string().optional(),
  // Web-research bindings only.
  research_prompt: z.string().optional(),
  /**
   * Maps SQL placeholder tokens (`:region`) to JSON Pointer paths into the
   * dashboard state (`/filters/region`). The refresh endpoint substitutes
   * the live state value into the SQL at run time — the agent does not
   * regenerate the query when a filter changes.
   */
  params_from_state: z.record(z.string(), z.string()).optional(),
  last_success: z.string().optional(),
  last_error: z.string().nullable().optional(),
  /**
   * Optional natural-language assertions the agent emits alongside the
   * binding. Evaluated by `evaluateBindingAssertions` against the
   * binding's last result set; failures surface in the devtools panel
   * + admin alert UI (not yet wired). Empty / absent = no assertions.
   */
  assertions: z.array(bindingAssertionSchema).optional(),
})

export type QueryBinding = z.infer<typeof queryBindingSchema>

/**
 * Watch binding — fires a registered action when the value at a state
 * path changes. The non-SQL companion to `queryBindings`: covers
 * cascading-filter resets, drill-to triggers, derived-state writes,
 * and any other state-change side-effect that doesn't reduce to "re-run
 * a SQL query".
 *
 * Stored at the spec root (not inside the element) so the AI follows
 * the same root-level binding-table idiom as queryBindings, and so the
 * runtime can wire watchers up before any element is mounted. Watchers
 * fire only on change — never on the initial mount.
 */
export const watchBindingSchema = z.object({
  /** Stable id; lets the dispatcher de-duplicate watchers across runs. */
  binding_id: z.string(),
  /**
   * JSON-Pointer path to watch (e.g. "/filters/region"). Object keys
   * only — array indices are NOT supported. To observe individual
   * elements of a list, restructure state as a keyed object
   * (`{by_id: {...}}`) and watch `/by_id/<key>` instead. See
   * `readJsonPointer` for the deviation rationale.
   */
  state_path: z.string(),
  /** Action name from the actions catalog (see actions-catalog.ts). */
  action: z.string(),
  /**
   * Params passed to the action handler. Values may be expressions
   * (`{$state:"/filters/region"}`, etc.) — they're resolved against
   * the current state at fire time.
   */
  params: z.record(z.string(), z.unknown()).optional(),
})

export type WatchBinding = z.infer<typeof watchBindingSchema>

export const dashboardSpecSchema = z.object({
  title: z.string().optional(),
  children: z.array(dashboardElementSchema),
  /** Internal exactly-once marker for the latest relative AI edit. */
  _lastIncrementalEditKey: z.string().optional(),
  state: z.record(z.string(), z.unknown()).optional(),
  sourcePrompt: z.string().optional(),
  dashboardSessionId: z.string().optional(),
  queryBindings: z.array(queryBindingSchema).optional(),
  /** Non-SQL state→action bindings — see watchBindingSchema. */
  watchBindings: z.array(watchBindingSchema).optional(),
  sourceContext: z
    .object({
      searchServices: z.array(z.string()).optional(),
      sourceUrls: z.array(z.string()).optional(),
      trustedMode: z.enum(["only", "preferred"]).optional(),
      documentIds: z.array(z.string()).optional(),
      citations: z
        .object({
          webUrls: z.array(z.string()).optional(),
          libraryChunks: z.array(z.string()).optional(),
        })
        .optional(),
    })
    .optional(),
})

export type DashboardSpec = z.infer<typeof dashboardSpecSchema>
export type DashboardElement = z.infer<typeof dashboardElementSchema>

// Resolved element types — these represent the shape AFTER expression resolution,
// so all fields are primitive values (no expression objects).
type DataPoint = Record<string, string | number | null>

export type BarChartElement = {
  type: "BarChart"
  title?: string
  data: DataPoint[]
  xKey: string
  bars: {
    dataKey: string
    color?: string
    label?: string
    stackId?: string
    thresholds?: ColorThreshold[]
  }[]
  layout?: "horizontal" | "vertical"
  onClickFilter?: ChartClickFilter
  drillThrough?: DrillThrough
  visible?: unknown
}
export type LineChartElement = {
  type: "LineChart"
  title?: string
  data: DataPoint[]
  xKey: string
  lines: { dataKey: string; color?: string; label?: string; strokeDasharray?: string }[]
  onClickFilter?: ChartClickFilter
  visible?: unknown
}
export type AreaChartElement = {
  type: "AreaChart"
  title?: string
  data: DataPoint[]
  xKey: string
  areas: { dataKey: string; color?: string; label?: string; stackId?: string }[]
  visible?: unknown
}
export type PieChartElement = {
  type: "PieChart"
  title?: string
  data: { name: string; value: number; color?: string }[]
  innerRadius?: number
  outerRadius?: number
  onClickFilter?: ChartClickFilter
  drillThrough?: DrillThrough
  visible?: unknown
}
export type KPICardElement = {
  type: "KPICard"
  title: string
  value: string | number
  unit?: string
  change?: number
  changeLabel?: string
  icon?: string
  sparkline?: number[]
  sparkline_color?: string
  thresholds?: ColorThreshold[]
  visible?: unknown
}
export type TableElement = {
  type: "Table"
  title?: string
  columns: { key: string; label: string; align?: "left" | "center" | "right" }[]
  rows: Record<string, string | number | null>[]
  drillThrough?: DrillThrough
  visible?: unknown
}
export type TextElement = {
  type: "Text"
  content: string
  variant?: "heading" | "subheading" | "body" | "caption"
  visible?: unknown
}
export type ResearchSummaryElement = {
  type: "ResearchSummary"
  title?: string
  content?: string
  sources?: { title?: string; url: string }[]
  visible?: unknown
}
export type GridElement = {
  type: "Grid"
  columns?: number
  gap?: number
  children: DashboardElement[]
  visible?: unknown
}
export type RowElement = {
  type: "Row"
  gap?: number
  children: DashboardElement[]
  visible?: unknown
}
export type SeparatorElement = {
  type: "Separator"
  label?: string
  visible?: unknown
}
export type AlertElement = {
  type: "Alert"
  message: string
  title?: string
  variant?: "info" | "warning" | "success" | "error"
  visible?: unknown
}
export type ProgressBarElement = {
  type: "ProgressBar"
  value: number
  max?: number
  label?: string
  color?: string
  visible?: unknown
}
export type TabsElement = {
  type: "Tabs"
  tabs: { label: string; value: string; children: DashboardElement[] }[]
  defaultValue?: string
  visible?: unknown
}
export type AccordionElement = {
  type: "Accordion"
  items: { title: string; children: DashboardElement[] }[]
  visible?: unknown
}
export type SelectElement = {
  type: "Select"
  stateKey: string
  label?: string
  options: { label: string; value: string }[]
  defaultValue?: string
  placeholder?: string
  visible?: unknown
}
export type CheckboxElement = {
  type: "Checkbox"
  stateKey: string
  label: string
  defaultValue?: boolean
  visible?: unknown
}
export type TextInputElement = {
  type: "TextInput"
  stateKey: string
  label?: string
  placeholder?: string
  defaultValue?: string
  visible?: unknown
}

/**
 * Generates a catalog description string for the AI system prompt,
 * documenting available dashboard components and their schemas.
 */
export function getCatalogDescription(): string {
  return `Available dashboard components:

1. **Grid** - Layout container with configurable columns (1-4)
   Props: { type: "Grid", columns?: number, gap?: number, children: Element[] }

2. **Row** - Horizontal layout container
   Props: { type: "Row", gap?: number, children: Element[] }

3. **BarChart** - Bar chart with multiple series
   Props: { type: "BarChart", title?: string, data: Record[], xKey: string, bars: [{ dataKey, color?, label?, stackId? }], layout?: "horizontal"|"vertical" }

4. **LineChart** - Line chart with multiple series
   Props: { type: "LineChart", title?: string, data: Record[], xKey: string, lines: [{ dataKey, color?, label?, strokeDasharray? }] }

5. **AreaChart** - Area chart with multiple series
   Props: { type: "AreaChart", title?: string, data: Record[], xKey: string, areas: [{ dataKey, color?, label?, stackId? }] }

6. **PieChart** - Pie or donut chart
   Props: { type: "PieChart", title?: string, data: [{ name, value, color? }], innerRadius?: number, outerRadius?: number }

7. **KPICard** - Key performance indicator card
   Props: { type: "KPICard", title: string, value: string|number, unit?: string, change?: number, changeLabel?: string }

8. **Table** - Data table
   Props: { type: "Table", title?: string, columns: [{ key, label, align? }], rows: Record[] }

9. **Text** - Text block
   Props: { type: "Text", content: string, variant?: "heading"|"subheading"|"body"|"caption" }

10. **Separator** - Visual divider between sections
    Props: { type: "Separator", label?: string }

11. **Alert** - Highlighted message for insights or warnings
    Props: { type: "Alert", message: string, title?: string, variant?: "info"|"warning"|"success"|"error" }

12. **ProgressBar** - Progress or goal completion indicator
    Props: { type: "ProgressBar", value: number, max?: number, label?: string, color?: string }

13. **Tabs** - Tabbed container to organize content into switchable views
    Props: { type: "Tabs", tabs: [{ label, value, children: Element[] }], defaultValue?: string }

14. **Accordion** - Collapsible sections to show/hide content
    Props: { type: "Accordion", items: [{ title, children: Element[] }] }

15. **Select** - Dropdown selector (writes to dashboard state)
    Props: { type: "Select", stateKey: string, label?: string, options: [{ label, value }], defaultValue?: string, placeholder?: string }

16. **Checkbox** - Toggle checkbox (writes to dashboard state)
    Props: { type: "Checkbox", stateKey: string, label: string, defaultValue?: boolean }

17. **TextInput** - Text input field (writes to dashboard state)
    Props: { type: "TextInput", stateKey: string, label?: string, placeholder?: string, defaultValue?: string }

Wrap your dashboard JSON in a \`\`\`json-render code block. The root object must have: { title?: string, children: Element[], state?: Record<string, any> }`
}
